<script setup lang="ts">
import $ from 'jquery';
import { ref } from 'vue';

import { ajaxWaiting, errutil, ORDER_PAYMENT_METHOD } from '#public/commons-global';

import { PAYMENTS, STYLE } from './globals';
import type { PerformSubmit, ProductFormData, ProductFormState } from './types';

type PaymentFormValues = {
    form?: Record<string, unknown>;
};

type PaymentRedirectData = Record<string, unknown> & {
    style?: string;
    product_id?: string;
    payment_method?: string;
};

type OrderPayload = {
    id_fe_order?: string | number;
    order_number?: string | number;
    order_date?: string;
};

type PaymentResponse = {
    method?: string;
    paypal?: PaymentFormValues;
    gestpay?: PaymentFormValues;
    none?: PaymentRedirectData;
    bank?: PaymentRedirectData;
    payload?: OrderPayload;
};

const paypalForm = ref<HTMLFormElement>();
const gestpayForm = ref<HTMLFormElement>();

const paypalAction = PAYMENTS?.paypal?.epr ? `https://${PAYMENTS.paypal.epr}/cgi-bin/webscr` : '';
const gestpayAction = PAYMENTS?.gestpay?.epr ?? '';

function submitForm(form: HTMLFormElement | undefined, data: PaymentFormValues) {
    if(!form) {
        return;
    }
    $(form).empty();
    const formdata = data.form ?? {};
    for(const name in formdata) {
        $('<input type="hidden"/>').appendTo(form).attr('name', name).val(formdata[name] as string | number);
    }
    form.submit();
}

function paypal(data: PaymentFormValues) {
    submitForm(paypalForm.value, data);
}

function gestpay(data: PaymentFormValues) {
    submitForm(gestpayForm.value, data);
}

function none(data: PaymentRedirectData, formData: ProductFormData, formState: ProductFormState) {
    if(formState.redirectTo) {
        window.open(formState.redirectTo, '_top');
        return;
    }
    data.style = STYLE;
    data.product_id = formData.Product_Id;
    data.payment_method = 'none';
    window.open(`result.shtml?${$.param(data)}`, '_top');
}

function bank(data: PaymentRedirectData, formData: ProductFormData, formState: ProductFormState) {
    if(formState.redirectTo) {
        window.open(formState.redirectTo, '_top');
        return;
    }
    if(formState.paymentNoneForceHistoryBack) {
        window.history.back();
        return;
    }
    data.style = STYLE;
    data.product_id = formData.Product_Id;
    data.payment_method = 'bank';
    window.open(`result.shtml?${$.param(data)}`, '_top');
}

function bankExists(data: PaymentResponse, formState: ProductFormState) {
    if(formState.redirectTo) {
        errutil.box('Ordine già registrato');
        return;
    }
    const order = data.payload;
    const params = {
        style: STYLE,
        payment_method: data.method,
        order_id: order?.id_fe_order,
        order_number: order?.order_number,
        order_date: new Date(order?.order_date ?? '').toLocaleString(),
    };
    window.open(`result.shtml?${$.param(params)}`, '_top');
}

const performSubmit: PerformSubmit = (formData, formState, epr = 'registerproduct.cmd') => {
    const submitData: Record<string, string> = {};
    for(const [name, value] of Object.entries(formData)) {
        // simulate old form submit
        if(typeof value === 'boolean') {
            submitData[name] = value ? 'on' : 'off';
        } else if((value !== undefined) && (value !== null)) {
            submitData[name] = value;
        }
    }
    // fields hidden by an unchecked checkbox must be submitted empty
    const hiddenFields: [boolean | undefined, string[]][] = [
        [formState.Product_Promotion_Code_flag, ['Product_Promotion_Code']],
        [formData.Service_FE_send_email_flag, ['Service_FE_send_email', 'Service_FE_send_email_2']],
    ];
    for(const [checked, fields] of hiddenFields) {
        if(checked) {
            continue;
        }
        for(const field of fields) {
            if(field in submitData) {
                submitData[field] = '';
            }
        }
    }
    if((formData.category_cliente === 'prof-altro') && formData.category_cliente_altro) {
        submitData['category_cliente'] = formData.category_cliente_altro;
    }
    delete submitData['category_cliente_altro'];
    $.ajax(ajaxWaiting({
        type: 'POST',
        url: `${__ROOT__}${epr}`,
        data: submitData,
        dataType: 'json',
        success(data: PaymentResponse) {
            if(errutil.ajaxerr(data)) {
                return;
            }
            if(data.method == ORDER_PAYMENT_METHOD.PAYPAL) {
                paypal(data.paypal ?? {});
                return;
            }
            if(data.method == ORDER_PAYMENT_METHOD.GESTPAY) {
                gestpay(data.gestpay ?? {});
                return;
            }
            if(data.method == ORDER_PAYMENT_METHOD.NONE) {
                none(data.none ?? {}, formData, formState);
                return;
            }
            if(data.method == ORDER_PAYMENT_METHOD.BANK_EXISTS) {
                bankExists(data, formState);
                return;
            }
            bank(data.bank ?? {}, formData, formState);
        },
        error(_jqXHR, textStatus, errorThrown) {
            errutil.box(textStatus, errorThrown);
        },
    }));
};

defineExpose({ performSubmit });
</script>

<template>
    <div v-if="PAYMENTS?.paypal?.epr" style="display: none;">
        <form
            v-once
            id="paypal-form"
            ref="paypalForm"
            :action="paypalAction"
            method="post"
            target="_top"
        />
    </div>
    <div v-if="PAYMENTS?.gestpay?.epr" style="display: none;">
        <form
            v-once
            id="gestpay-form"
            ref="gestpayForm"
            :action="gestpayAction"
            method="post"
            target="_top"
        />
    </div>
</template>
